import React, {useMemo} from "react";
import {RemoteValue} from "../../../util/RemoteValue";
import {useDeskStore} from "../../../api/DeskStore";
import {useEmployeeStore} from "../../../api/EmployeeStore";
import {useResidenceStore} from "../../../api/ResidenceStore";
import {associateBy} from "../../../util/MapUtil";
import {idFn} from "../../../util/FunctionUtil";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import DeskChip from "../../../components/desk-chip/DeskChip";
import EmployeeAvatar from "../../../components/employee-avatar/EmployeeAvatar";
import EmployeeLink from "../../../components/employee-link/EmployeeLink";
import {SiteChip} from "../../../components/site-chip/SiteChip";
import {Desks_Read, Employees_Read, Offices_Read, WorkSites_Read} from "../../../util/access/Permission";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {Desk, Employee} from "../../../api/Model";
import {useWorkSiteMapStore, WorkSites} from "../../../api/WorkSiteMapStore";
import {useFixDesksAssignedToNonResidents} from "./useFixDesksAssignedToNonResidents";

type DeskAssignedToNonResidentProblem = {desk: Desk, employee: Employee};

const desksAssignedToNonResidentsCsvModel = (workSites: WorkSites) => new CsvModel<DeskAssignedToNonResidentProblem>([
    {header: 'Desk', value: ({desk}) => desk.name},
    {header: 'Room', value: ({desk}) => desk.roomName},
    {header: 'Person', value: ({employee}) => employee.fullName},
    {header: 'Email', value: ({employee}) => employee.email},
    {header: 'Hibob Site', value: ({employee}) => workSites.residenceByEmployee(employee).workSite.name},
    {header: 'Hibob Office', value: ({employee}) => workSites.residenceByEmployee(employee).hibobOffice?.name ?? ''},
]);

export function DesksAssignedToNonResidentsWidget() {
    const pageUrl = usePageUrl();
    const desks = useDeskStore(state => state.remoteValue);
    const employees = useEmployeeStore(state => state.remoteValue);
    const residency = useResidenceStore().remoteValue;
    const workSites = useWorkSiteMapStore(state => state.remoteValue);
    const problems = useMemo(() => RemoteValue.combine({desks, employees, residency}).map(values => {
        const employeesById = associateBy(values.employees, idFn);
        return values.desks.filter(desk => {
            const employee = desk.assignedTo && employeesById.get(desk.assignedTo.id);
            return employee != null && !values.residency.isResident(employee);
        }).map(desk => ({desk, employee: employeesById.get(desk.assignedTo!.id)!}));
    }), [desks, employees, residency]);
    const csvModel = useMemo(() => workSites.map(
        workSites => desksAssignedToNonResidentsCsvModel(workSites)
    ), [workSites]);
    const fixAction = useFixDesksAssignedToNonResidents(problems);

    return (
        <DashboardWidget
            title="Desks assigned to non-residents"
            description="Sometimes, an employee relocates to another office but their desk stays assigned to them in the former office."
            problems={problems}
            requiredPermissions={[Desks_Read, Employees_Read, Offices_Read, WorkSites_Read]}
            link={pageUrl(AppPages.desks, {}, {problem: 'not-resident'})}
            csvModel={csvModel}
            fixAction={fixAction}
            renderProblem={({desk, employee}, index) => (
                <DashboardWidgetItem
                    key={desk.id}
                    index={index}
                    left={
                        <>
                            <DeskChip desk={desk} deskAssignee={employee} hideAssignee size="small"/>
                            <EmployeeAvatar employee={employee} sx={{width: 24, height: 24}}/>
                            <EmployeeLink employee={employee}/>
                            <SiteChip employee={employee}/>
                        </>
                    }
                />
            )}
        />
    );
}
